/**
 * `PredictionProvider` per `specs/10-contracts.md`.
 *
 * Overdue: the contract says to define every provider interface in Phase 1 "even where only one
 * implementation exists", and `TtsProvider`, `InputAdapter` and `SyncProvider` all landed there. This
 * one was missed, so it arrives with its first implementation instead (C41).
 */

export type CandidateSource = 'lexicon' | 'personal' | 'bigram' | 'phrase' | 'abbrev';

export interface Candidate {
  word: string;
  /**
   * Stable slot index — **part of the contract, not an implementation detail** (§5.3).
   *
   * If a candidate returned for prefix `p` is still valid for `p + c`, it must come back with the same
   * slot. Reordering on every keystroke is common practice in predictive keyboards and wrong here: it
   * forces the person to re-scan and re-aim, which is precisely the cost prediction is supposed to be
   * saving them. With an unknown visual impairment that cost is higher still.
   */
  slot: number;
  source: CandidateSource;
}

export interface SuggestInput {
  /** What has been typed of the current word. Empty means "suggest a next word". */
  prefix: string;
  /** The completed word before the caret, for bigram context. */
  prevWord: string | null;
  count: number;
}

export interface PredictionProvider {
  readonly id: string;
  ready(): Promise<void>;
  suggest(input: SuggestInput): Promise<Candidate[]>;
}

/**
 * Personal counts, supplied by the caller rather than read by the provider.
 *
 * The provider runs in a worker and must not touch IndexedDB — the main thread owns storage, and a
 * worker reaching for it would both duplicate the connection and put a database read on the < 100 ms
 * path. Empty in stage 1; dominant from stage 2 (2.3).
 */
/**
 * **Every key here is folded through `normaliseForMatch`.** `LearningService` does it on the way in.
 *
 * It has to be somewhere, and the writer is the only place it can be done once per sentence rather than
 * once per candidate per keystroke. Keying on the raw token was a real bug, not a tidiness question: the
 * keyboard used to type in capitals, so typing taught the counts `WASSER` while the lexicon offered
 * `Wasser`, and the personal terms — the two the scorer weights highest — never fired for a single word
 * the person had spelled out. Folding also merges the sentence-initial `Wie` with the mid-sentence
 * `wie`, which would otherwise be two vocabularies of one word each.
 */
export interface PersonalCounts {
  unigrams: ReadonlyMap<string, number>;
  bigrams: ReadonlyMap<string, number>;
  /**
   * Word → **epoch ms of** the last time it was spoken, for the recency term.
   *
   * An absolute timestamp, not an age: `scoreCandidate` computes `nowMs - lastUsed` itself, so a map of
   * ages would decay backwards and a word said an hour ago would look like one said in 1970.
   */
  lastUsed: ReadonlyMap<string, number>;
  /**
   * Folded key → how the person actually spells it, so a name keeps its capital.
   *
   * Needed because folding is lossy and the folded form is not fit to show: a personal word the corpus
   * has never seen is offered from this map, and `ruedi` in the row for a person called Ruedi would be
   * the feature insulting them once per sentence. Ties go to the spelling seen most often.
   */
  display: ReadonlyMap<string, string>;
}

export const EMPTY_PERSONAL: PersonalCounts = {
  unigrams: new Map(),
  bigrams: new Map(),
  lastUsed: new Map(),
  display: new Map(),
};

/**
 * Key for the personal bigram map. One place, so the worker and the writer cannot disagree.
 *
 * A space is unambiguous because `splitWords` cannot produce a token containing one. The separator is
 * purely in-memory — the store keys on `[prev, next]` and the export writes tuples — so nothing on disk
 * depends on it, and nothing anywhere splits the key back apart.
 */
export const bigramKey = (prev: string, next: string): string => `${prev} ${next}`;
